import React, { FC, useEffect, useRef, useState } from "react";
import {
    Input,
    Modal,
    Row,
    Col,
    Button,
    Table,
    Switch,
    Space,
    Spin,
} from "antd";
import { CloseOutlined, SearchOutlined, EditOutlined, EyeOutlined } from "@ant-design/icons";
import _ from "lodash";
import { useDispatch, useSelector } from "react-redux";
import { useMediaQuery } from "react-responsive";

import {
    GET_CUSTOMERS_LIST,
    UPDATE_CUSTOMER_BLOCK_STATUS,
} from "reducers/authReducer";
import {
    GET_CUSTOMERS_LIST as GET_CUSTOMERS_LIST_ACTION,
    UPDATE_CUSTOMER_BLOCK_STATUS as UPDATE_CUSTOMER_BLOCK_STATUS_ACTION
} from "reducers/authReducer";
import { RootState } from "reducers/rootReducer";
import {
    wrapRequestPayload,
} from "../../helpers/request/RequestPayload";
import { SocketTopics } from "../../constants/enums";

import { generateUUID } from "utils/appUtil";

let timeout;

const CustomerListModal: FC<{ onClose: () => void; socket?: ISocket }> = ({
    onClose,
    socket,
}) => {
    const { customersList, customersListLoading } = useSelector(
        (state: RootState) => state.auth
    );

    const { projects } = useSelector((state: RootState) => state.dashboard);
    const { requestIds } = useSelector((state: RootState) => state.dashboard);
    const isMobileScreen = useMediaQuery({ query: "(max-width: 700px)" });

    const [isModalVisible, setIsModalVisible] = useState(true);
    const [searchText, setSearchText] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [isSearching, setIsSearching] = useState(false);
    const [isPaginationLoading, setIsPaginationLoading] = useState(false);

    const dispatch = useDispatch();

    const handleCancel = () => {
        setIsModalVisible(false);
        onClose && onClose();
    };

    const escapeRedis = (str: string) => {
        const replacements: { [key: string]: string } = {
            ',': '\\,',
            '.': '\\.',
            '<': '\\<',
            '>': '\\>',
            '{': '\\{',
            '}': '\\}',
            '[': '\\[',
            ']': '\\]',
            '"': '\\"',
            "'": "\\'",
            ':': '\\:',
            ';': '\\;',
            '!': '\\!',
            '@': '\\@',
            '#': '\\#',
            $: '\\$',
            '%': '\\%',
            '^': '\\^',
            '&': '\\&',
            '*': '\\*',
            '(': '\\(',
            ')': '\\)',
            '-': '\\-',
            '+': '\\+',
            '=': '\\=',
            '~': '\\~',
        };

        return str.replace(
            /,|\.|<|>|\{|\}|\[|\]|"|'|:|;|!|@|#|\$|%|\^|&|\*|\(|\)|-|\+|=|~/g,
            x => {
                return replacements[x];
            }
        );
    };

    const fetchCustomers = (search = "", page = 1, size = 10) => {
        dispatch(GET_CUSTOMERS_LIST_ACTION({ search, page, size }));

        const serviceId = localStorage.getItem("selectedOrg") || projects?.userServiceId;
        console.log('service id', { serviceId });

        let customerFilter: any = {};
        if (search && search.length > 2) {
            const searchIndex = search.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
            customerFilter = {
                filter: {
                    expression: `(exists(@name) && contains(lower(@name),lower('${searchIndex}'))) || (exists(@email) && contains(lower(@email),lower('${searchIndex}'))) || (exists(@phone) && contains(lower(@phone),lower('${searchIndex}'))) || (exists(@id) && contains(lower(@id),lower('${searchIndex}'))) || (exists(@objectId) && contains(lower(@objectId),lower('${searchIndex}')))`,
                },
            };
        }

        const getCustomerPayload = {
            select: {
                index: {
                    searchIndex: `${process.env.REACT_APP_ECO_SYSTEM}:custom:id`,
                    searchQuery: `@partitions:{org\\:${escapeRedis(serviceId)}}`,
                    searchOptions: 'VERBATIM TIMEOUT 10000',
                },
                load: {
                    info: '$.info',
                    objectId: '$.objectId',
                    name: '$..info..name',
                    email: '$..info..email',
                    phone: '$..info..phone',
                    id: '$..info.id',
                    createdBy: '$.createdBy',
                },
            },
            ...customerFilter,
            result: {
                pagination: {
                    offset: (page - 1) * size,
                    limit: size,
                },
            },
        };

        console.log('Customer payload:', getCustomerPayload);
        console.log('Service ID:', serviceId);
        console.log('Projects:', projects);

        socket.publishToEvent(
            SocketTopics.PublishPageTopics,
            `${process.env.REACT_APP_ECO_SYSTEM}/connection/${localStorage.getItem(
                "CID"
            )}/service/custom-bds/aggregateCustom`,
            wrapRequestPayload(getCustomerPayload, requestIds.customer)
        );
    };

    useEffect(() => {
        if (projects?.userServiceId) {
            fetchCustomers();
        }
    }, [projects?.userServiceId]);


    // Reset searching state when customers list changes (whether results or no results)
    useEffect(() => {
        if (isSearching && customersList.length >= 0) {
            // Add a delay to ensure the loader is visible for at least 1 second
            setTimeout(() => {
                setIsSearching(false);
            }, 1000);
        }
    }, [customersList, isSearching]);

    // Reset pagination loading state when customers list changes
    useEffect(() => {
        if (isPaginationLoading && customersList.length >= 0) {
            // Add a delay to ensure the loader is visible for at least 1 second
            setTimeout(() => {
                setIsPaginationLoading(false);
            }, 1000);
        }
    }, [customersList, isPaginationLoading]);

    // Subscribe to customer data responses when modal opens
    useEffect(() => {
        if (socket && isModalVisible) {
            const subscriptionTopic = `${process.env.REACT_APP_ECO_SYSTEM}/connection/${localStorage.getItem(
                "CID"
            )}/service/custom-bds/aggregateCustom/reply`;

            socket.subscribeToEvent(
                SocketTopics.SubscribePageTopics,
                subscriptionTopic
            );

            // Cleanup subscription when modal closes
            return () => {
                socket.unSubscribeToEvent(
                    SocketTopics.SubscribePageTopics,
                    subscriptionTopic
                );
            };
        }
    }, [socket, isModalVisible]);


    const handleSearch = (value: string) => {
        setSearchText(value);
        if (timeout) clearTimeout(timeout);
        timeout = setTimeout(() => {
            setIsSearching(true);
            fetchCustomers(value, currentPage, pageSize);

            // Reset searching state after 5 seconds if no response
            setTimeout(() => {
                if (isSearching) {
                    setIsSearching(false);
                }
            }, 5000);
        }, 500);
    };

    const handleBlockToggle = (customerId: string, isBlocked: boolean) => {
        console.log('Block toggle clicked:', { customerId, isBlocked });
        dispatch(UPDATE_CUSTOMER_BLOCK_STATUS_ACTION({ customerId, isBlocked }));

        // Send WebSocket message to update customer block status on server
        if (socket) {
            const pagePayload: any = {
                objectId: customerId,
                changes: [
                    {
                        op: 'add',
                        path: '/info/blockedAt',
                        value: isBlocked ? Date.now() : undefined,
                    },
                ],
            };

            console.log('Sending block status update:', pagePayload);

            socket.publishToEvent(
                SocketTopics.PublishPageTopics,
                `${process.env.REACT_APP_ECO_SYSTEM}/connection/${localStorage.getItem(
                    "CID"
                )}/service/custom-bds/updateCustom`,
                wrapRequestPayload(pagePayload, requestIds.customer)
            );
        }
    };

    const handleEdit = (customer: any) => {
        console.log('Edit customer:', customer);

        // Get required parameters from localStorage
        const userId = localStorage.getItem("RID");
        const connectionId = localStorage.getItem("CID");
        const serviceId = localStorage.getItem("selectedOrg");
        const customerId = customer._id;

        // Construct the edit URL
        const editUrl = `${process.env.REACT_APP_SHARED_URI}/customer-cpc?userId=${userId}&connectionId=${connectionId}&serviceId=${serviceId}&customerId=${customerId}`;

        console.log('Opening edit URL:', editUrl);

        // Open in new tab
        window.open(editUrl, '_blank');
    };

    const handleViewProfile = (customer: any) => {
        // TODO: Get config user ID from BE
        const userId = 103176;
        const connectionId = localStorage.getItem("CID");
        const serviceId = localStorage.getItem("selectedOrg") || projects?.userServiceId;
        const objectId = customer?.objectId || customer?.objectid || customer?._id;
        const customerId = customer?.customerId || customer?.id || customer?._id;

        const profileUrl = `${process.env.REACT_APP_SHARED_URI}/customer-profile?userId=${userId}&connectionId=${connectionId}&serviceId=${serviceId}&objectid=${objectId}&customerId=${customerId}`;
        window.open(profileUrl, "_blank");
    };

    const columns = [
        {
            title: 'ID',
            dataIndex: 'customerId',
            key: 'customerId',
            width: 100,
        },
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
            width: 150,
        },
        {
            title: 'Email',
            dataIndex: 'email',
            key: 'email',
            width: 200,
        },
        {
            title: 'Phone',
            dataIndex: 'phone',
            key: 'phone',
            width: 150,
        },
        {
            title: 'Created By',
            dataIndex: 'createdBy',
            key: 'createdBy',
            width: 120,
            render: (createdBy: string) => createdBy || 'admin',
        },
        {
            title: 'Blocked',
            dataIndex: 'isBlocked',
            key: 'isBlocked',
            width: 100,
            render: (isBlocked: boolean, record: any) => (
                <Switch
                    checked={!!isBlocked}
                    onChange={(checked) => handleBlockToggle(record._id, checked)}
                    size="small"
                />
            ),
        },
        {
            title: 'Action',
            key: 'action',
            width: 150,
            render: (text: any, record: any) => (
                <Space direction="horizontal" size={0}>
                    <Button
                        type="link"
                        icon={<EditOutlined />}
                        onClick={() => handleEdit(record)}
                        size="middle"
                    >
                    </Button>
                    <Button
                        type="link"
                        icon={<EyeOutlined />}
                        onClick={() => handleViewProfile(record)}
                        size="middle"
                    >
                    </Button>
                </Space>
            ),
        },
    ];

    const handleTableChange = (pagination: any) => {
        setCurrentPage(pagination.current);
        setPageSize(pagination.pageSize);
        setIsPaginationLoading(true);
        fetchCustomers(searchText, pagination.current, pagination.pageSize);
    };


    return (
        <Modal
            title=""
            visible={isModalVisible}
            onOk={() => { }}
            onCancel={handleCancel}
            width={isMobileScreen ? "95%" : "1200px"}
            className="beam-modal customer-list-modal"
            footer={[]}
            closable={false}
            destroyOnClose={true}
            style={{ top: isMobileScreen ? 8 : 24 }}
            bodyStyle={{ height: "100%", overflow: "hidden", padding: "16px" }}
        >
            <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
                <Row className="d-flex mt-1 mb-4">
                    <Col span={24}>
                        <h3>
                            Customers
                            <button onClick={onClose} className="pull-right b-0 rm-b">
                                <CloseOutlined />
                            </button>
                        </h3>
                    </Col>
                </Row>

                <Row className="mb-4">
                    <Col span={24}>
                        <Input
                            placeholder="Search customers by name, email, phone, or ID"
                            prefix={<SearchOutlined />}
                            onChange={(e) => handleSearch(e.target.value)}
                            style={{ width: '100%' }}
                        />
                    </Col>
                </Row>

                <Row style={{ flex: 1, minHeight: 0, height: "100%" }}>
                    <Col span={24}>
                        {(customersListLoading || isSearching || isPaginationLoading) ? (
                            <div style={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                height: '300px',
                                flexDirection: 'column'
                            }}>
                                <Spin size="large" />
                                <p style={{ color: '#666', fontSize: '16px', marginTop: '16px' }}>Loading customers...</p>
                            </div>
                        ) : customersList.length === 0 ? (
                            <div style={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                height: '300px',
                                flexDirection: 'column'
                            }}>
                                <p style={{ color: '#666', fontSize: '16px' }}>No customers found</p>
                                <p style={{ color: '#999', fontSize: '14px', marginTop: '8px' }}>
                                    Please check your service configuration.
                                </p>
                            </div>
                        ) : (
                            <Table
                                columns={columns}
                                dataSource={customersList}
                                loading={false}
                                rowKey="_id"
                                pagination={{
                                    current: currentPage,
                                    pageSize: pageSize,
                                    total: customersList.length,
                                    showSizeChanger: true,
                                    showQuickJumper: true,
                                    showTotal: (total, range) =>
                                        `${range[0]}-${range[1]} of ${total} customers`,
                                }}
                                onChange={handleTableChange}
                                scroll={{ x: true, y: isMobileScreen ? "50vh" : "55vh" }}
                                size="small"
                            />
                        )}
                    </Col>
                </Row>

                {/* <Row className="mt-3">
                    <Col span={24}>
                        <div style={{ background: "#fff", padding: "8px 0", borderTop: "1px solid #eee", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <strong>Filters</strong>
                            <span style={{ opacity: 0.7 }}>—</span>
                        </div>
                    </Col>
                </Row> */}
            </div>
        </Modal>
    );
};

export default CustomerListModal;
